import type { Payload, TextFieldSingleValidation, Where } from 'payload'

export const orgUrlInUseMessage =
  'This website URL is already in use. The organization you are trying to add may already be listed.'

/**
 * Returns a comparable website URL: trimmed, no scheme or leading www, lowercased host and path.
 */
export function canonicalWebsiteUrl(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null

  const withScheme =
    trimmed.startsWith('http://') || trimmed.startsWith('https://') ? trimmed : `https://${trimmed}`

  try {
    const url = new URL(withScheme)
    const host = url.hostname.toLowerCase().replace(/^www\./, '')
    const path = `${url.pathname.replace(/\/+$/, '')}${url.search}`.toLowerCase()
    return `${host}${path}`
  } catch {
    return trimmed.toLowerCase()
  }
}

/**
 * Builds comparable website URL strings for duplicate lookups.
 */
export function websiteUrlLookupValues(value: string): string[] {
  const trimmed = value.trim()
  if (!trimmed) return []

  const withScheme =
    trimmed.startsWith('http://') || trimmed.startsWith('https://') ? trimmed : `https://${trimmed}`

  let host = trimmed.toLowerCase()
  let path = ''
  try {
    const url = new URL(withScheme)
    host = url.hostname.toLowerCase().replace(/^www\./, '')
    path = url.pathname.replace(/\/+$/, '') + url.search
  } catch {
    return [trimmed, trimmed.toLowerCase()]
  }

  const values = new Set<string>([trimmed, trimmed.toLowerCase()])
  for (const hostname of [host, `www.${host}`]) {
    values.add(hostname + path)
    for (const scheme of ['http', 'https']) {
      const url = `${scheme}://${hostname}${path}`
      values.add(url)
      values.add(`${url}/`)
    }
  }

  return [...values]
}

/**
 * Returns whether another organization already uses this website URL.
 */
export async function orgUrlExists({
  payload,
  websiteUrl,
  excludeId,
}: {
  payload: Payload
  websiteUrl: string
  excludeId?: number | string
}): Promise<boolean> {
  const canonical = canonicalWebsiteUrl(websiteUrl)
  const variants = websiteUrlLookupValues(websiteUrl)
  if (!canonical || variants.length === 0) return false

  const where: Where = {
    and: [{ or: variants.map((variant) => ({ websiteUrl: { contains: variant } })) }],
  }

  if (excludeId !== undefined) {
    where.and!.push({ id: { not_equals: excludeId } })
  }

  const result = await payload.find({
    collection: 'organizations',
    where,
    pagination: false,
    depth: 0,
    overrideAccess: true,
    select: { websiteUrl: true },
  })

  return result.docs.some((doc) => canonicalWebsiteUrl(doc.websiteUrl ?? '') === canonical)
}

/**
 * Payload field validate that rejects a website URL already used by another organization.
 */
export const validateOrgUrl: TextFieldSingleValidation = async (value, { req, id }) => {
  if (!value || typeof value !== 'string' || !value.trim()) return true

  const exists = await orgUrlExists({
    payload: req.payload,
    websiteUrl: value,
    excludeId: id,
  })

  return exists ? orgUrlInUseMessage : true
}
